import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, notification } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import EditableCell from '../EditableCell';
import TableActionButtons from '../TableActionButtons';
import DataDestructuring from '../../../../helpers/DataDestructuring';
import { CustomTable, CheckboxReverse, Label } from '../../../../styles/sharedUIElemStyles';

const { addKey, addColumnsTranslations, countTotal, createReadOnlyColumns } = DataDestructuring;
const { Summary } = CustomTable;

export interface Props {
  value: Form.CustomTableFormValue;
  index: string;
  defaultValue: Form.CustomTableFormValue;
  updateFormData: (value: any) => void;
  readOnly: boolean;
  template: any;
}
/**
 *  Komponent tabeli form zajęć z polami edycji typu input, checkbox i percent
 *
 */
export const FormyZajecTable = (props: Props): React.ReactElement => {
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState('');
  const [checked, setChecked] = useState(false);
  const { t } = useTranslation();

  const isEditing = (record: any): boolean => record.godzKont > 0 && record.key === editingKey;
  const checkELearning = (e: CheckboxChangeEvent): void => {
    setChecked(e.target.checked);
  };
  const edit = (record: any): void => {
    setChecked(record.elearning.props.checked);
    setTimeout(() => {
      form.setFieldsValue({
        godzKont: '',
        udzialWagowy: '',
        ...record,
      });
      form.resetFields(['elearning']);
      setEditingKey(record.key);
    }, 200);
  };
  const confirm = async (record: any): Promise<void> => {
    const modifiedCells = await form.validateFields();
    const row = { ...record, ...modifiedCells, elearning: checked };
    const newData = addKey(props.value.data);
    const index = newData.findIndex(item => record.key === item.key);
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, { ...item, ...row });
      const updatedValue = { ...props.value, data: newData };
      props.updateFormData(updatedValue);
      setEditingKey('');
      const totalPercentage = countTotal(newData, 'udzialWagowy');
      if (totalPercentage !== 100) {
        notification.error({
          message: t('FORMY_ZAJEC_INFO_SUMA_WAG_ROWNA_100'),
          top: 100,
        });
      }
    } else {
      setEditingKey('');
    }
  };
  const colsWithTln = addColumnsTranslations(props.template.columns);
  const actionColumns = [...colsWithTln];
  actionColumns.push({
    title: '',
    dataIndex: 'actions',
    width: '10%',
    sorter: false,
    // eslint-disable-next-line react/display-name
    render: (_: any, record: any): React.ReactElement | null => {
      const editable = isEditing(record);
      return record.godzKont > 0 ? (
        <TableActionButtons
          editable={editable}
          onChangeConfirmation={confirm.bind(null, record)}
          onChangeCancel={(): void => setEditingKey('')}
          onEdit={edit.bind(null, record)}
          disabled={editingKey !== ''}
        />
      ) : null;
    },
  });

  const mergedColumns = actionColumns.map((col: SingleColumn) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: any): any => ({
        record,
        inputType: col.type,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        checked: record.elearning.props ? record.elearning.props.checked : record.elearning,
        onChecked: checkELearning,
      }),
    };
  });
  const mapCheckboxInData = (array: Form.CustomTableData[]): any[] => {
    const mappedData = array.map(item =>
      item.godzKont > 0
        ? {
            ...item,
            elearning: !props.readOnly ? (
              <CheckboxReverse checked={item.elearning} />
            ) : item.elearning ? (
              <CheckOutlined />
            ) : (
              <CloseOutlined />
            ),
          }
        : { ...item },
    );
    return mappedData;
  };
  const dataWithKey = addKey(props.value.data);
  const dataWithCheckbox = mapCheckboxInData(dataWithKey);
  const defaultDataWithKey = addKey(props.defaultValue.data);
  const defaultDataWithCheckbox = mapCheckboxInData(defaultDataWithKey);

  return (
    <>
      {!props.readOnly ? <Label>{t('BEFORE_CHANGE')}</Label> : null}
      <CustomTable
        dataSource={props.readOnly ? dataWithCheckbox : defaultDataWithCheckbox}
        columns={!props.readOnly ? createReadOnlyColumns(colsWithTln) : colsWithTln}
        rowClassName='editable-row'
        pagination={false}
        className={!props.readOnly ? 'table__readOnly' : ''}
        summary={(): React.ReactNode => {
          return (
            <Summary.Row>
              <Summary.Cell index={0}>{t('TOTAL')}</Summary.Cell>
              <Summary.Cell index={1}>{countTotal(defaultDataWithKey, 'godzKont')}</Summary.Cell>
              <Summary.Cell index={2}></Summary.Cell>
              <Summary.Cell index={3}>{countTotal(defaultDataWithKey, 'udzialWagowy')}%</Summary.Cell>
              {!props.readOnly ? <Summary.Cell index={4}></Summary.Cell> : null}
            </Summary.Row>
          );
        }}
      />
      {!props.readOnly ? (
        <Form form={form} component={false} initialValues={{ elearning: checked }}>
          <CustomTable
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            bordered
            dataSource={dataWithCheckbox}
            columns={mergedColumns}
            rowClassName='editable-row'
            pagination={false}
            summary={(): React.ReactNode => {
              const totalPercentage = countTotal(dataWithKey, 'udzialWagowy');
              return (
                <Summary.Row>
                  <Summary.Cell index={0}>{t('TOTAL')}</Summary.Cell>
                  <Summary.Cell index={1}>{countTotal(dataWithKey, 'godzKont')}</Summary.Cell>
                  <Summary.Cell index={2}></Summary.Cell>
                  <Summary.Cell index={3} className={totalPercentage !== 100 ? 'cell__info_error' : ''}>
                    {totalPercentage}%
                  </Summary.Cell>
                  <Summary.Cell index={4}></Summary.Cell>
                </Summary.Row>
              );
            }}
          />
        </Form>
      ) : null}
    </>
  );
};

export default FormyZajecTable;
